import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/core/hooks/useAuth";
import { Input } from "@/shared/components/Input";
import { Button } from "@/shared/components/Button";
import { AuthCardLayout } from "@/shared/components/AuthCardLayout";
import { sanitizarCpf, cpfEhValido } from "@/core/utils/cpf";

const loginSchema = z.object({
  cpf: z
    .string()
    .min(1, "Informe seu CPF.")
    .refine((valor) => cpfEhValido(valor), "CPF inválido. Use apenas os 11 números."),
  senha: z.string().min(1, "Informe sua senha."),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [erroLogin, setErroLogin] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  async function onSubmit(dados: LoginFormData) {
    setErroLogin(null);
    try {
      await login({ cpf: sanitizarCpf(dados.cpf), senha: dados.senha });
      navigate("/", { replace: true });
    } catch (erro) {
      setErroLogin(erro instanceof Error ? erro.message : "Erro ao entrar. Tente novamente.");
    }
  }

  return (
    <AuthCardLayout
      tagline="Gestão da igreja, em um só lugar"
      titulo="Entrar"
      subtitulo="Acesse com seu CPF e senha"
      rodape={
        <>
          Não tem conta?{" "}
          <Link to="/cadastro" className="font-bold text-primary hover:underline">
            Criar conta
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
        <Input
          label="CPF"
          placeholder="000.000.000-00"
          inputMode="numeric"
          autoComplete="username"
          erro={errors.cpf?.message}
          {...register("cpf")}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, "").slice(0, 11);
            e.target.value = value;
          }}
        />
        <Input
          label="Senha"
          type="password"
          autoComplete="current-password"
          erro={errors.senha?.message}
          {...register("senha")}
        />
        <Link
          to="/esqueci-senha"
          className="-mt-1 self-end text-xs font-semibold text-primary hover:underline"
        >
          Esqueci minha senha
        </Link>

        {erroLogin && (
          <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-xs text-danger">
            {erroLogin}
          </p>
        )}

        <Button type="submit" carregando={isSubmitting} className="mt-1">
          Entrar
        </Button>
      </form>
    </AuthCardLayout>
  );
}
